import React, { useEffect, useRef, useState } from 'react';

import '@styles/configure/maintenance.scss';
import Tooltip from '@components/tooltip';
import Typography from '@components/typography';
import { updateIncludeMaintenance } from '@store/reducers/vehiclesearchparams';
import { MaintenanceCheckIcon } from '@components/maintenancecheckicon';
import { useAppDispatch } from '@hooks/statehooks';
import { v4 as uuid4 } from 'uuid';
import { useAppSelector } from '@hooks/statehooks';
import { usePricing } from '@hooks/usepricing';
import { MaintenanceProps } from 'interface';
import { maintenanceTooltip } from '../../../resources';

const Maintenance: React.FC<MaintenanceProps> = ({
  'data-cy': dataCy,
  items,
}): JSX.Element => {
  const dispatch = useAppDispatch();

  const searchParams = useAppSelector((state) => state.vehicleSearchParams);
  const { pricingType } = useAppSelector((state) => state.vehicleInformation);
  const { pricing } = useAppSelector((state) => state.vehiclePrice);
  const { updateAllPricing } = usePricing();

  const [maintenanceChecked, setMaintenanceChecked] = useState(
    searchParams.include_maintenance || false
  );

  const onChangeHandler = (e: React.FormEvent<HTMLInputElement>) => {
    const checkedState: boolean = e.currentTarget.checked;

    setMaintenanceChecked(checkedState);
    dispatch(updateIncludeMaintenance(checkedState));
  };

  const isMounted = useRef(false);

  useEffect(() => {
    if (isMounted.current) {
      updateAllPricing(pricingType);
    } else {
      isMounted.current = true;
    }
  }, [maintenanceChecked]);

  const maintenanceItem = (label: string, index: number): JSX.Element => {
    return (
      <li
        key={uuid4()}
        className="maintenance__coverage-item"
        data-cy={`${dataCy}-maintenance__coverage-item-${index}`}
      >
        <MaintenanceCheckIcon />
        <Typography variant="body1" size="sm">
          {label}
        </Typography>
      </li>
    );
  };

  return (
    <div className="maintenance__container" data-cy={dataCy}>
      <div className="maintenance__header" data-cy={`${dataCy}-header`}>
        <Typography variant="caption" size="lg" colour="primary" weight="bold">
          Include Peace of Mind
        </Typography>
      </div>
      <div className="maintenance__content" data-cy={`${dataCy}-content`}>
        <div className="maintenance__checkbox" data-cy={`${dataCy}-checkbox`}>
          <input
            type="checkbox"
            className="maintenance__checkbox-input"
            id="maintenance-checkbox"
            onChange={onChangeHandler}
            checked={maintenanceChecked}
            data-cy={`${dataCy}-checkbox-input`}
          />
          <svg
            className="maintenance__checkbox-icon"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              className="maintenance__checkbox-icon-outline"
              x="0.5"
              y="0.5"
              width="23"
              height="23"
              rx="2.5"
              fill="white"
            />
            <path
              className="maintenance__checkbox-icon-tick"
              d="M6 13.65L9.42857 17.25L18 8.25"
              stroke="#008576"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <label
            htmlFor="maintenance-checkbox"
            data-cy={`${dataCy}-checkbox-label`}
          >
            <Typography
              className="maintenance__label"
              variant="body1"
              size="rg"
              weight="semi-bold"
              colour="primary"
            >
              Add Maintenance
            </Typography>
          </label>
        </div>
        <Tooltip
          id="maintenance-tooltip"
          text={''}
          delayHide={100}
          data-cy={`${dataCy}-tooltip`}
        >
          <Typography variant="caption" size="xs" colour="alternative">
            {maintenanceTooltip}
          </Typography>
        </Tooltip>
        <Typography
          className="maintenance__price"
          variant="body1"
          size="sm"
          colour="primary"
        >
          {`£${pricing?.maintCost}pm`}
        </Typography>
      </div>
      <div className="maintenance__coverage" data-cy={`${dataCy}-coverage`}>
        <Typography
          className="maintenance__coverage-title"
          variant="body1"
          size="sm"
          weight="semi-bold"
          colour="primary"
        >
          Maintenance covers:
        </Typography>
        <ul className="maintenance__coverage-items">
          {items.map((value, index) => maintenanceItem(value, index))}
        </ul>
      </div>
    </div>
  );
};

export default Maintenance;
